// src/utils/dashboardAggregations.ts — SCE Issues
// Fonctions PURES de calcul des agrégats du Dashboard.
//
// Aucune dépendance React, aucun side effect, aucune lecture du DOM ni de
// localStorage. Tout est testable unitairement.
//
// Convention :
//   - Les fonctions prennent un Issue[] (déjà filtré) en entrée
//   - Elles retournent des structures de données prêtes à être rendues
//   - Aucune ne choisit de couleur : c'est le rôle du composant, qui peut
//     alors utiliser les jetons CSS (les barres sont des <div>, pas du SVG)
//
// ─── Lot 5 : l'axe principal devient le type d'équipement ───
//
//   Type (3) ──→ Catégorie (5) ──→ Modèle (52) ──→ Équipement (N° de série)
//
// Type et catégorie se lisent sur le problème (field_9). Le modèle vient du
// référentiel SCE_Equipements : l'appelant fournit `modeleDe`, pour que ce
// module reste sans réseau.

import {
  A_QUALIFIER,
  ANNULE,
  BADGES_CLOTURE,
  CRITICITES_ORDRE,
  SANS_BADGE,
  STATUTS_ORDRE,
  SUJETS,
  cleRapprochement,
  estActif,
  type FiltreBadgeCloture,
  type Issue,
  type Metier,
  type Statut,
} from '@/types/issue.types'
import { METIERS_ORDRE } from '@/config/metiers.config'
import { rangeeMetiersEtSujets } from '@/config/sujets.config'
import { TYPE_PAR_CATEGORIE } from '@/config/equipements.catalogue'

// ─── Périodes ───

export type Periode = 'week' | 'month' | 'quarter' | 'all'

export const PERIODES_ORDRE: readonly Periode[] = ['week', 'month', 'quarter', 'all']

export const PERIODE_LABELS: Record<Periode, string> = {
  week: '7 jours',
  month: '30 jours',
  quarter: '90 jours',
  all: 'Tout',
}

/**
 * Retourne la date de début de la période sélectionnée (incluse).
 * Pour 'all' → renvoie null (pas de borne basse).
 */
export function getPeriodeStart(periode: Periode, now: Date = new Date()): Date | null {
  if (periode === 'all') return null
  const start = new Date(now)
  start.setHours(0, 0, 0, 0)
  if (periode === 'week') start.setDate(start.getDate() - 7)
  else if (periode === 'month') start.setDate(start.getDate() - 30)
  else if (periode === 'quarter') start.setDate(start.getDate() - 90)
  return start
}

/**
 * Filtre un Issue[] par période sur dateDeclaration.
 * Garde toutes les issues si periode = 'all'.
 */
export function filterByPeriode(
  issues: Issue[],
  periode: Periode,
  now: Date = new Date(),
): Issue[] {
  const start = getPeriodeStart(periode, now)
  if (!start) return issues
  const startMs = start.getTime()
  return issues.filter((i) => {
    if (!i.dateDeclaration) return false
    const t = new Date(i.dateDeclaration).getTime()
    return !Number.isNaN(t) && t >= startMs
  })
}

// ─── KPIs ───

export interface DashboardKpis {
  total: number
  /** Ouvert + En cours : ce qui attend encore une action. */
  actifs: number
  /** Critiques ET actifs. */
  critiques: number
  /** Actifs dont la famille OU la criticité reste « À qualifier ». */
  aQualifier: number
}

// Règles des KPI, exportées pour que la liste ouverte au clic sur un KPI
// contienne exactement les problèmes comptés.
//
// `estActif` et non `statut !== 'Clos'` : un annulé n'est ni un critique
// ouvert, ni du travail à qualifier. Il garde volontairement ses sentinelles
// 'À qualifier' (il a été écarté, pas qualifié).
export function estCritiqueActif(i: Issue): boolean {
  return estActif(i) && i.criticite === 'Critique'
}

export function estAQualifierActif(i: Issue): boolean {
  return estActif(i) && (i.famille === A_QUALIFIER || i.criticite === A_QUALIFIER)
}

/** Famille pas encore renseignée — les annulés, écartés et non qualifiés, n'y sont pas. */
export function estFamilleNonQualifiee(i: Issue): boolean {
  return (i.famille === A_QUALIFIER || !i.famille) && i.statut !== ANNULE
}

export function computeKpis(issues: Issue[]): DashboardKpis {
  return {
    total: issues.length,
    actifs: issues.filter(estActif).length,
    critiques: issues.filter(estCritiqueActif).length,
    aQualifier: issues.filter(estAQualifierActif).length,
  }
}

// ─── Axe équipement ───

/** Valeur d'un problème dont on ne connaît pas le type, la catégorie ou le modèle. */
export const NON_IDENTIFIE = 'Non identifié'

export type NiveauEquipement = 'type' | 'categorie' | 'modele' | 'equipement'

/** La partie « équipement » des filtres : ce que le graphique fait descendre. */
export interface SelectionEquipement {
  types: readonly string[]
  categories: readonly string[]
  modeles: readonly string[]
}

export const SELECTION_VIDE: SelectionEquipement = { types: [], categories: [], modeles: [] }

/** Modèle d'un problème, lu dans le référentiel par l'appelant. */
export type ModeleDe = (issue: Issue) => string

export function typeDe(issue: Issue): string {
  return (TYPE_PAR_CATEGORIE as Partial<Record<string, string>>)[issue.familleEquipement] ?? NON_IDENTIFIE
}

export function categorieDe(issue: Issue): string {
  return issue.familleEquipement || NON_IDENTIFIE
}

/**
 * Niveau affiché par le graphique : celui qui suit la dernière valeur UNIQUE
 * choisie. Deux types cochés dans le tiroir = on reste au niveau type, en
 * n'affichant que ces deux-là.
 */
export function niveauEquipement(sel: SelectionEquipement): NiveauEquipement {
  if (sel.modeles.length === 1) return 'equipement'
  if (sel.categories.length === 1) return 'modele'
  if (sel.types.length === 1) return 'categorie'
  return 'type'
}

function cleDuNiveau(niveau: NiveauEquipement, modeleDe: ModeleDe): (i: Issue) => string {
  switch (niveau) {
    case 'type':
      return typeDe
    case 'categorie':
      return categorieDe
    case 'modele':
      return modeleDe
    case 'equipement':
      return (i) => cleRapprochement(i.numeroSerie)
    default: {
      const jamais: never = niveau
      return jamais
    }
  }
}

/** Le problème passe-t-il la sélection équipement ? */
export function dansSelectionEquipement(
  issue: Issue,
  sel: SelectionEquipement,
  modeleDe: ModeleDe,
): boolean {
  if (sel.types.length && !sel.types.includes(typeDe(issue))) return false
  if (sel.categories.length && !sel.categories.includes(categorieDe(issue))) return false
  if (sel.modeles.length && !sel.modeles.includes(modeleDe(issue))) return false
  return true
}

/**
 * Descend d'un niveau en choisissant une barre.
 *
 * Un niveau qui n'aurait qu'une seule barre n'apprend rien : on le traverse.
 * « Colonne » (type) ne contient que « Colonne » (catégorie), donc un clic
 * sur le type Colonne mène directement à ses modèles.
 *
 * @param visibles les problèmes affichés AVANT le clic
 */
export function descendreEquipement(
  sel: SelectionEquipement,
  valeur: string,
  visibles: readonly Issue[],
  modeleDe: ModeleDe,
): SelectionEquipement {
  const niveau = niveauEquipement(sel)
  let suivante: SelectionEquipement
  if (niveau === 'type') suivante = { types: [valeur], categories: [], modeles: [] }
  else if (niveau === 'categorie') suivante = { ...sel, categories: [valeur], modeles: [] }
  else if (niveau === 'modele') suivante = { ...sel, modeles: [valeur] }
  else return sel

  const cle = cleDuNiveau(niveau, modeleDe)
  const restants = visibles.filter((i) => cle(i) === valeur)
  for (let n = niveauEquipement(suivante); n === 'categorie' || n === 'modele'; n = niveauEquipement(suivante)) {
    const valeurs = new Set(restants.map(cleDuNiveau(n, modeleDe)))
    if (valeurs.size !== 1) break
    const [unique] = [...valeurs] as [string]
    suivante = n === 'categorie'
      ? { ...suivante, categories: [unique], modeles: [] }
      : { ...suivante, modeles: [unique] }
  }
  return suivante
}

export interface EtapeAriane {
  label: string
  /** Sélection à appliquer pour revenir à cette étape. */
  cible: SelectionEquipement
}

/**
 * Fil d'Ariane de la descente. Deux niveaux de même libellé (le type
 * Colonne et la catégorie Colonne) n'en font qu'un : l'étape mène au plus
 * profond des deux, puisque le moins profond n'aurait qu'une seule barre.
 * La dernière étape est la position courante.
 */
export function filAriane(sel: SelectionEquipement): EtapeAriane[] {
  const etapes: EtapeAriane[] = [{ label: 'Tous les types', cible: SELECTION_VIDE }]
  function ajouter(label: string, cible: SelectionEquipement): void {
    const derniere = etapes[etapes.length - 1]
    if (derniere && etapes.length > 1 && derniere.label === label) derniere.cible = cible
    else etapes.push({ label, cible })
  }
  const [type] = sel.types
  const [categorie] = sel.categories
  const [modele] = sel.modeles
  if (sel.types.length === 1 && type) ajouter(type, { types: sel.types, categories: [], modeles: [] })
  if (sel.categories.length === 1 && categorie) ajouter(categorie, { ...sel, modeles: [] })
  if (sel.modeles.length === 1 && modele) ajouter(modele, sel)
  return etapes
}

// ─── Barres empilées du graphique principal ───

export type SerieCouleur = 'metier' | 'criticite' | 'statut'

/** Valeurs de chaque série, dans l'ordre d'empilement et de légende. */
export const VALEURS_SERIE: Record<SerieCouleur, readonly string[]> = {
  // GOULOTTE (23/10) : son propre segment, juste après MECA (hachuré Méca).
  metier: rangeeMetiersEtSujets(METIERS_ORDRE, SUJETS),
  criticite: CRITICITES_ORDRE,
  statut: STATUTS_ORDRE,
}

export function valeurSerie(issue: Issue, serie: SerieCouleur): string {
  if (serie === 'metier') return issue.sujet ?? issue.metier
  if (serie === 'criticite') return issue.criticite
  return issue.statut
}

export interface SegmentBarre {
  valeur: string
  n: number
}

export interface BarreEquipement {
  /** Valeur de filtre (pour un équipement : sa clé de rapprochement). */
  valeur: string
  label: string
  /** Au niveau équipement : « PN · modèle ». */
  detail?: string
  total: number
  segments: SegmentBarre[]
}

export function computeBarresEquipement(
  issues: readonly Issue[],
  niveau: NiveauEquipement,
  modeleDe: ModeleDe,
  serie: SerieCouleur,
): BarreEquipement[] {
  const cle = cleDuNiveau(niveau, modeleDe)
  const groupes = new Map<string, Issue[]>()
  for (const i of issues) {
    const k = cle(i)
    const g = groupes.get(k)
    if (g) g.push(i)
    else groupes.set(k, [i])
  }
  return [...groupes.entries()]
    .map(([valeur, groupe]) => {
      const premier = groupe[0] as Issue
      return {
        valeur,
        // Au niveau équipement la clé est normalisée : on affiche la
        // première graphie rencontrée.
        label: niveau === 'equipement' ? premier.numeroSerie : valeur,
        ...(niveau === 'equipement' ? { detail: `${premier.pn} · ${modeleDe(premier)}` } : {}),
        total: groupe.length,
        segments: VALEURS_SERIE[serie]
          .map((v) => ({ valeur: v, n: groupe.filter((i) => valeurSerie(i, serie) === v).length }))
          .filter((s) => s.n > 0),
      }
    })
    .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label, 'fr'))
}

// ─── Familles de problème ───

export interface FamilleStat {
  famille: string
  metier: Metier
  n: number
}

/**
 * Familles de problème les plus fréquentes — le cœur des revues TQC.
 *
 * Groupées par (métier, famille) : ELEC et AUTOM partagent des libellés
 * (« Adresse PLC »…) qui ne désignent pas le même défaut.
 * Les « À qualifier » ne sont pas une famille : comptés à part.
 */
export function computeTopFamilles(
  issues: readonly Issue[],
  n = 6,
): { familles: FamilleStat[]; nonQualifies: number; totalQualifies: number } {
  const compte = new Map<string, FamilleStat>()
  let nonQualifies = 0
  let totalQualifies = 0
  for (const i of issues) {
    if (i.famille === A_QUALIFIER || !i.famille) {
      if (estFamilleNonQualifiee(i)) nonQualifies += 1
      continue
    }
    totalQualifies += 1
    const k = `${i.metier}|${i.famille}`
    const c = compte.get(k)
    if (c) c.n += 1
    else compte.set(k, { famille: i.famille, metier: i.metier, n: 1 })
  }
  const familles = [...compte.values()]
    .sort((a, b) => b.n - a.n || a.famille.localeCompare(b.famille, 'fr'))
    .slice(0, n)
  return { familles, nonQualifies, totalQualifies }
}

// ─── Badges de clôture ───

export interface RepartitionBadges {
  lignes: { badge: FiltreBadgeCloture; n: number }[]
  nbClos: number
  /** Somme des barres : dépasse nbClos dès qu'un problème porte deux badges. */
  totalBadges: number
}

/**
 * Un problème FNC-T + TQC compte dans les DEUX barres (arbitrage Lot 5) :
 * la question posée est « combien de problèmes sont passés par ce circuit ».
 * Les clos sans badge (d'avant le Lot 3) ont leur ligne, s'il y en a.
 */
export function computeRepartitionBadges(issues: readonly Issue[]): RepartitionBadges {
  const clos = issues.filter((i) => i.statut === 'Clos')
  const lignes: RepartitionBadges['lignes'] = BADGES_CLOTURE.map((badge) => ({
    badge,
    n: clos.filter((i) => i.badgesCloture?.includes(badge)).length,
  }))
  const sansBadge = clos.filter((i) => !i.badgesCloture?.length).length
  if (sansBadge > 0) lignes.push({ badge: SANS_BADGE, n: sansBadge })
  return {
    lignes,
    nbClos: clos.length,
    totalBadges: lignes.reduce((s, l) => s + (l.badge === SANS_BADGE ? 0 : l.n), 0),
  }
}

// ─── Équipements récurrents ───

export interface EquipementRecurrent {
  numeroSerie: string
  of: string
  pn: string
  modele: string
  count: number
  /** Répartition par statut, dans l'ordre du cycle de vie, sans les zéros. */
  parStatut: { statut: Statut; n: number }[]
}

/**
 * Top N équipements récurrents (plus d'un problème).
 *
 * ⚠️ Groupement sur la clé NORMALISÉE, affichage sur la graphie d'origine :
 * sur la graphie brute, « 4626-SC » et « 4626-sc » comptaient pour deux
 * équipements d'une occurrence chacun, et l'équipement disparaissait du
 * tableau sous le seuil `count > 1`.
 */
export function computeTopEquipements(
  issues: readonly Issue[],
  n: number,
  modeleDe: ModeleDe,
): EquipementRecurrent[] {
  const map = new Map<string, { eq: EquipementRecurrent; statuts: Statut[] }>()
  for (const i of issues) {
    const cle = cleRapprochement(i.numeroSerie)
    if (!cle) continue
    const existing = map.get(cle)
    if (existing) {
      existing.eq.count += 1
      existing.statuts.push(i.statut)
    } else {
      map.set(cle, {
        eq: { numeroSerie: i.numeroSerie, of: i.of, pn: i.pn, modele: modeleDe(i), count: 1, parStatut: [] },
        statuts: [i.statut],
      })
    }
  }
  return [...map.values()]
    .filter(({ eq }) => eq.count > 1)
    .sort((a, b) => b.eq.count - a.eq.count)
    .slice(0, n)
    .map(({ eq, statuts }) => ({
      ...eq,
      parStatut: STATUTS_ORDRE
        .map((statut) => ({ statut, n: statuts.filter((s) => s === statut).length }))
        .filter((s) => s.n > 0),
    }))
}

// ─── Séries temporelles ───

export interface BucketStat {
  /** Libellé court "Avr 26" */
  label: string
  /** Date de début du bucket (utile pour tooltip et tri) */
  start: Date
  value: number
}

/**
 * Déclarations par mois sur N mois glissants, du plus ancien au plus récent.
 */
export function computeMonthlyBuckets(
  issues: readonly Issue[],
  nbMois = 12,
  now: Date = new Date(),
): BucketStat[] {
  const buckets: BucketStat[] = []
  const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1)

  for (let i = nbMois - 1; i >= 0; i--) {
    const start = new Date(currentMonth)
    start.setMonth(start.getMonth() - i)
    buckets.push({ start, label: formatMonthShort(start), value: 0 })
  }

  for (const issue of issues) {
    if (!issue.dateDeclaration) continue
    const d = new Date(issue.dateDeclaration)
    if (Number.isNaN(d.getTime())) continue
    const issueMonth = new Date(d.getFullYear(), d.getMonth(), 1).getTime()
    const bucket = buckets.find((b) => b.start.getTime() === issueMonth)
    if (bucket) bucket.value += 1
  }

  return buckets
}

const MOIS_COURT = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']

export function formatMonthShort(d: Date): string {
  const m = MOIS_COURT[d.getMonth()] ?? ''
  // Année à 2 chiffres pour densité mobile : "Avr 26"
  const y = String(d.getFullYear()).slice(-2)
  return `${m} ${y}`
}
